/**
 * Collects the N most recent battle-log thumbnail slots (same order as drawer rows),
 * walking from the newest log entry backward until `limit` thumbnails are gathered.
 */
import {
  getBattleActionLogAttackThumbHealthLines,
  getBattleActionLogResourceDeltaLinesForDrawer
} from './battleActionLogResourceDeltas';

function primaryEntity(entry) {
  return entry.unit || entry.order || entry.attacker || entry.drone || entry.weapon || entry.strategem || entry.source;
}

/**
 * @param {object} entry
 * @param {string|number|null} userId
 * @returns {Array<{ kind: 'order', orderLike: object, showSkull?: boolean, healthDelta?: object, intelDelta?: object, generalDelta?: object } | { kind: 'general', targetPlayerId: string|number }>}
 */
export function getOrderedThumbnailDescriptorsForEntry(entry, userId) {
  if (!entry || typeof entry !== 'object') return [];
  const primary = primaryEntity(entry);
  const pid = entry.playerId;

  const attackThumbHealthLines = getBattleActionLogAttackThumbHealthLines(entry);
  const { attackColumns } = getBattleActionLogResourceDeltaLinesForDrawer(entry);

  const pushOrder = (out, orderLike, extras = {}) => {
    if (!orderLike || (orderLike.id == null && !orderLike.instanceId)) return;
    out.push({ kind: 'order', orderLike, ...extras });
  };

  switch (entry.type) {
    case 'order-received-hand': {
      if (pid == null || String(pid) !== String(userId)) return [];
      if (!primary) return [];
      return [{ kind: 'order', orderLike: primary }];
    }
    case 'turn-passed':
    case 'turn-started':
      return [];
    case 'barrier-lost': {
      const lostPid = entry.playerId;
      if (lostPid == null) return [];
      return [{ kind: 'general', targetPlayerId: lostPid }];
    }
    case 'counter-strike-played': {
      const out = [];
      pushOrder(out, primary);
      if (entry.target && !entry.target.isGeneral) {
        pushOrder(out, entry.target);
      }
      return out;
    }
    case 'unit-deployed':
      return primary ? [{ kind: 'order', orderLike: primary }] : [];
    case 'weapon-equipped': {
      const out = [];
      pushOrder(out, entry.weapon || primary);
      if (entry.target) pushOrder(out, entry.target);
      return out;
    }
    case 'weapon-stat-bonus': {
      const out = [];
      if (entry.weapon) pushOrder(out, entry.weapon);
      if (entry.target) pushOrder(out, entry.target);
      return out;
    }
    case 'drone-deployed':
      return primary || entry.drone
        ? [{ kind: 'order', orderLike: entry.drone || primary }]
        : [];
    case 'drone-charge-used': {
      const out = [];
      pushOrder(out, entry.drone || primary);
      if (entry.target) pushOrder(out, entry.target);
      return out;
    }
    case 'drone-aura-applied':
      return entry.drone ? [{ kind: 'order', orderLike: entry.drone }] : [];
    case 'drone-exhausted-deploy': {
      const out = [];
      if (entry.drone) pushOrder(out, entry.drone);
      if (entry.spawnedUnit) pushOrder(out, entry.spawnedUnit);
      return out;
    }
    case 'ability-relay-grant': {
      const out = [];
      if (entry.drone) pushOrder(out, entry.drone);
      if (entry.target) pushOrder(out, entry.target);
      return out;
    }
    case 'unit-attacked': {
      const out = [];
      pushOrder(out, entry.attacker || primary, {
        showSkull: entry.attackerDestroyed,
        healthDelta: attackThumbHealthLines[0],
        intelDelta: attackColumns?.attackerIntel,
        generalDelta: attackColumns?.attackerGeneral
      });
      if (entry.target) {
        pushOrder(out, entry.target, {
          showSkull: entry.targetDestroyed,
          healthDelta: attackThumbHealthLines[1],
          intelDelta: attackColumns?.targetIntel,
          generalDelta: attackColumns?.targetGeneral
        });
      }
      return out;
    }
    case 'unit-redeem-damage':
    case 'unit-special-effect-damage': {
      const out = [];
      pushOrder(out, entry.unit || primary, { showSkull: false });
      if (entry.target) pushOrder(out, entry.target, { showSkull: entry.targetDestroyed });
      return out;
    }
    case 'unit-attacked-general': {
      const tgtPid = entry.targetPlayerId ?? entry.outcome?.targetPlayerId;
      const out = [];
      pushOrder(out, entry.attacker || primary, { showSkull: entry.attackerDestroyed });
      if (tgtPid != null) out.push({ kind: 'general', targetPlayerId: tgtPid });
      return out;
    }
    case 'penetrate-excess-general':
    case 'attack-effect-general-damage':
    case 'recover-healed-general': {
      const genPid = entry.generalPlayerId;
      const generalDelta =
        entry.affectedGeneralHealth &&
        typeof entry.affectedGeneralHealth.before === 'number' &&
        typeof entry.affectedGeneralHealth.after === 'number' &&
        entry.affectedGeneralHealth.before !== entry.affectedGeneralHealth.after
          ? { before: entry.affectedGeneralHealth.before, after: entry.affectedGeneralHealth.after }
          : null;
      const out = [];
      if (entry.attacker || entry.source) pushOrder(out, entry.attacker || entry.source);
      if (genPid != null) out.push({ kind: 'general', targetPlayerId: genPid, generalDelta });
      return out;
    }
    case 'ability-blitz':
    case 'ability-sniper-granted':
    case 'ability-shield-absorbed':
    case 'ability-raid':
    case 'ability-mend-wounds': {
      const out = [];
      pushOrder(out, entry.unit || primary, { showSkull: false });
      if (entry.source) pushOrder(out, entry.source, { showSkull: false });
      return out;
    }
    case 'ability-sniper-kill': {
      const out = [];
      pushOrder(out, entry.attacker || primary, { showSkull: false });
      if (entry.target) pushOrder(out, entry.target, { showSkull: true });
      return out;
    }
    case 'fell-intel': {
      const out = [];
      if (entry.attacker) pushOrder(out, entry.attacker);
      if (entry.target) pushOrder(out, entry.target);
      return out;
    }
    case 'fell-summon': {
      const out = [];
      if (entry.attacker) pushOrder(out, entry.attacker);
      if (entry.summonedUnit) pushOrder(out, entry.summonedUnit);
      return out;
    }
    case 'strategem-hive-surge-buff': {
      const out = [];
      if (primary) pushOrder(out, primary);
      for (const u of entry.targets || []) {
        pushOrder(out, u);
      }
      return out;
    }
    case 'strategem-damaged-general':
    case 'strategem-healed-general': {
      const tgtPid = entry.targetPlayerId;
      const out = [];
      if (primary) pushOrder(out, primary);
      if (tgtPid != null) out.push({ kind: 'general', targetPlayerId: tgtPid });
      return out;
    }
    default:
      return primary ? [{ kind: 'order', orderLike: primary }] : [];
  }
}

/**
 * @param {object[]} actionLog
 * @param {string|number|null} userId
 * @param {number} limit
 * @returns {Array<{ descriptor: { kind: 'order', orderLike: object, showSkull?: boolean, healthDelta?: object, intelDelta?: object, generalDelta?: object }, entryKey: string }>}
 */
export function collectRecentBattleActionLogThumbnails(actionLog, userId, limit) {
  if (!Number.isFinite(limit) || limit <= 0) return [];
  const list = Array.isArray(actionLog) ? actionLog : [];
  const collected = [];

  for (let i = list.length - 1; i >= 0 && collected.length < limit; i -= 1) {
    const entry = list[i];
    const entryKey =
      entry?.sequence != null ? `seq-${entry.sequence}` : `idx-${i}`;
    const descriptors = getOrderedThumbnailDescriptorsForEntry(entry, userId);
    for (let d = 0; d < descriptors.length && collected.length < limit; d += 1) {
      const descriptor = descriptors[d];
      if (descriptor.kind !== 'order') continue;
      collected.push({
        descriptor,
        entryKey: `${entryKey}-ord-${d}`
      });
    }
  }

  return collected;
}
